import { useCallback, useMemo, useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";

import {
    formatDateValue,
    getDocumentCategoryTitleKey,
    getDocumentDisplayLabel,
    getDocumentProfile,
    getDocumentStatus,
    groupDocuments,
    isUploadOnlyDocument,
    resolveDocumentCategory,
} from "utils/documentGroups";

import styles from "./styles.module.scss";

const getDocumentSourceLabel = (document = {}) =>
    document.status || document.file_name || document.label || "";

const getDocumentDownloadName = (document = {}) => {
    const sourceUrl = document.document_url || "";
    const sanitizedUrl = sourceUrl.split("?")[0];
    const urlFileName = sanitizedUrl.split("/").pop();

    if (urlFileName) {
        return urlFileName;
    }

    return document.displayLabel || document.file_name || "document";
};

const triggerDocumentDownload = (documentItem = {}) => {
    if (!documentItem.document_url) {
        return;
    }

    const link = window.document.createElement("a");
    link.href = documentItem.document_url;
    link.target = "_blank";
    link.rel = "noreferrer";
    link.setAttribute("download", getDocumentDownloadName(documentItem));
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
};

const getDocumentDetails = (document, t) => {
    if (isUploadOnlyDocument(document)) {
        return [];
    }

    const validFrom = formatDateValue(document.valid_from || document.validFrom);
    const validTo = formatDateValue(document.valid_to || document.validTo);
    const uploadedAt = formatDateValue(document.created_at || document.createdAt);
    const coverageAmount = document.coverage_amount || document.coverageAmount || null;
    const policyNumber = document.policy_number || document.policyNumber || null;
    const details = [];

    if (validFrom || validTo) {
        details.push({
            label: t("documents.validity_period"),
            value: [validFrom, validTo].filter(Boolean).join(" - "),
        });
    }

    if (uploadedAt) {
        details.push({
            label: t("documents.uploaded_at"),
            value: uploadedAt,
        });
    }

    if (coverageAmount) {
        details.push({
            label: t("documents.modal.coverage_amount"),
            value: coverageAmount,
        });
    }

    if (policyNumber) {
        details.push({
            label: t("documents.modal.policy_number"),
            value: policyNumber,
        });
    }

    return details;
};

export default function CompanyDocuments({
    documentsData,
    showExpectedDocuments = false,
    onRequestDocument,
    companyRole,
}) {
    const { t } = useTranslation();
    const [expandedDocumentIds, setExpandedDocumentIds] = useState({});

    const normalizedDocuments = useMemo(
        () =>
            (documentsData || []).map((document, index) => {
                const sourceLabel = getDocumentSourceLabel(document);
                const displayLabel = getDocumentDisplayLabel(sourceLabel);

                return {
                    ...document,
                    id: document.id || `${displayLabel}-${index}`,
                    sourceLabel,
                    displayLabel,
                    categoryKey: resolveDocumentCategory(sourceLabel),
                };
            }),
        [documentsData],
    );

    const sections = useMemo(() => {
        const { categoryOrder, requiredTypes } = getDocumentProfile(companyRole);

        if (!showExpectedDocuments) {
            return groupDocuments(normalizedDocuments, { includeEmptyCategories: false, companyRole }).map((section) => ({
                ...section,
                items: section.items.map((item) => ({
                    ...item,
                    isMissing: false,
                })),
                missingCount: 0,
            }));
        }

        return categoryOrder.map((categoryKey) => {
            const requiredLabels = requiredTypes[categoryKey] || [];
            const categoryDocuments = normalizedDocuments.filter((document) => document.categoryKey === categoryKey);
            const remainingDocuments = [...categoryDocuments];
            const items = [];

            requiredLabels.forEach((label) => {
                const documentIndex = remainingDocuments.findIndex((document) => document.displayLabel === label);

                if (documentIndex >= 0) {
                    items.push({
                        ...remainingDocuments[documentIndex],
                        isMissing: false,
                    });
                    remainingDocuments.splice(documentIndex, 1);
                    return;
                }

                items.push({
                    id: `missing-${categoryKey}-${label}`,
                    displayLabel: label,
                    categoryKey,
                    isMissing: true,
                });
            });

            remainingDocuments.forEach((document) => {
                items.push({
                    ...document,
                    isMissing: false,
                });
            });

            return {
                categoryKey,
                titleKey: getDocumentCategoryTitleKey(categoryKey),
                items,
                missingCount: items.filter((item) => item.isMissing).length,
            };
        });
    }, [normalizedDocuments, showExpectedDocuments, companyRole]);

    const availableDocuments = useMemo(
        () => sections.flatMap((section) => section.items.filter((item) => !item.isMissing && item.document_url)),
        [sections],
    );

    const toggleDocumentDetails = useCallback((documentId) => {
        setExpandedDocumentIds((currentState) => ({
            ...currentState,
            [documentId]: !currentState[documentId],
        }));
    }, []);

    const handlePreviewDocument = useCallback((documentUrl) => {
        if (!documentUrl) {
            return;
        }

        window.open(documentUrl, "_blank", "noopener,noreferrer");
    }, []);

    const handleDownloadAll = useCallback(() => {
        availableDocuments.forEach((documentItem, index) => {
            window.setTimeout(() => triggerDocumentDownload(documentItem), index * 120);
        });
    }, [availableDocuments]);

    // The same heading in both states, like the reviews tab of the popup — an empty tab that
    // only holds one line of text right against the edge of the panel reads as broken
    const header = (
        <div className={styles.header}>
            <h3 className={styles.headerTitle}>{t("documents.company_count", { count: availableDocuments.length })}</h3>
            {availableDocuments.length > 0 && (
                <button type="button" className={styles.downloadAll} onClick={handleDownloadAll}>
                    {t("documents.download_all")}
                </button>
            )}
        </div>
    );

    if (!availableDocuments.length) {
        return (
            <div className={styles.wrapper}>
                {header}
                <div className={styles.emptyState}>{t("documents.empty")}</div>
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            {header}

            <div className={styles.sections}>
                {sections.map((section) => (
                    <section className={styles.section} key={section.categoryKey}>
                        <header className={styles.sectionHeader}>
                            <div className={styles.sectionHeading}>
                                <span
                                    className={cn(styles.sectionIcon, styles[`sectionIcon--${section.categoryKey}`])}
                                    aria-hidden="true"
                                />
                                <h4 className={styles.sectionTitle}>{t(section.titleKey)}</h4>
                            </div>
                            {showExpectedDocuments && section.missingCount > 0 && (
                                <span className={styles.sectionMissing}>
                                    {t("documents.required_count", { count: section.missingCount })}
                                </span>
                            )}
                        </header>

                        <div className={styles.sectionItems}>
                            {section.items.map((item) => {
                                if (item.isMissing) {
                                    return (
                                        <article
                                            className={cn(styles.card, styles["card--missing"])}
                                            key={item.id}
                                        >
                                            <div className={styles.cardMain}>
                                                <div>
                                                    <h5 className={styles.cardTitle}>{item.displayLabel}</h5>
                                                    <p className={styles.cardHint}>{t("documents.not_provided")}</p>
                                                </div>
                                                {onRequestDocument && (
                                                    <button
                                                        type="button"
                                                        className={styles.requestButton}
                                                        onClick={() => onRequestDocument(item)}
                                                    >
                                                        {t("documents.request_document")}
                                                    </button>
                                                )}
                                            </div>
                                        </article>
                                    );
                                }

                                const isUploadOnly = isUploadOnlyDocument(item);
                                const status = getDocumentStatus(item, t);
                                const uploadedAt = isUploadOnly ? null : formatDateValue(item.created_at || item.createdAt);
                                const details = getDocumentDetails(item, t);
                                const isExpanded = Boolean(expandedDocumentIds[item.id]);
                                const canToggleDetails = details.length > 0;

                                return (
                                    <article className={cn(styles.card, status && styles[`card--${status.tone}`])} key={item.id}>
                                        <div className={styles.cardMain}>
                                            <div className={styles.cardBody}>
                                                <div className={styles.cardTopRow}>
                                                    <h5 className={styles.cardTitle}>{item.displayLabel}</h5>
                                                    {canToggleDetails && (
                                                        <button
                                                            type="button"
                                                            className={styles.detailToggle}
                                                            onClick={() => toggleDocumentDetails(item.id)}
                                                        >
                                                            {isExpanded
                                                                ? t("documents.hide_details")
                                                                : t("documents.show_details")}
                                                        </button>
                                                    )}
                                                </div>

                                                <div className={styles.metaRow}>
                                                    {status && (
                                                        <span
                                                            className={cn(
                                                                styles.statusBadge,
                                                                styles[`statusBadge--${status.tone}`],
                                                            )}
                                                        >
                                                            {status.label}
                                                        </span>
                                                    )}
                                                    {uploadedAt && (
                                                        <span className={styles.metaText}>
                                                            {t("documents.uploaded_on", { date: uploadedAt })}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className={styles.actions}>
                                                <button
                                                    type="button"
                                                    className={cn(styles.iconButton, styles["iconButton--view"])}
                                                    onClick={() => handlePreviewDocument(item.document_url)}
                                                    title={t("documents.open_document")}
                                                    aria-label={t("documents.open_document")}
                                                />
                                                <button
                                                    type="button"
                                                    className={cn(styles.iconButton, styles["iconButton--download"])}
                                                    onClick={() => triggerDocumentDownload(item)}
                                                    title={t("documents.download_document")}
                                                    aria-label={t("documents.download_document")}
                                                />
                                            </div>
                                        </div>

                                        {isExpanded && details.length > 0 && (
                                            <div className={styles.detailsGrid}>
                                                {details.map((detail) => (
                                                    <div className={styles.detailItem} key={`${item.id}-${detail.label}`}>
                                                        <span className={styles.detailLabel}>{detail.label}</span>
                                                        <span className={styles.detailValue}>{detail.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
}
